import { useCallback, useEffect, useRef, useState } from 'react';
import { SocketMgr } from './SocketMgr';
import { SocketPackTool } from './SocketPackTool';

export interface WSOptions {
  /**超时时间 默认值10s*/
  timeout?: number;
  /**超时回调 */
  timeOutCallback?: () => void;
}

type WsPromiseController<T> = {
  resolve?: (value: T) => void;
  reject?: (reason?: any) => void;
};

/**只收推送消息 */
export const useWs = <T>(rspId: number, decode: any, getCache = true) => {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<{ code?: number } | undefined>();
  const [target] = useState(Symbol());

  useEffect(() => {
    if (getCache) {
      setData(WsDataCenter.getInstance().getDataByPacktId(rspId, target));
    }
  }, []);
  useEffect(() => {
    SocketPackTool.register(
      function (data: T, code): void {
        if (code === 200) {
          WsDataCenter.getInstance().setData(rspId, data, target);
          setData(data || ({} as T));
          setError(undefined);
        } else {
          WsDataCenter.getInstance().setData(rspId, null, target);
          setError({ code: code });
        }
      },
      rspId,
      decode,
      target
    );
    return () => {
      SocketPackTool.unregister(rspId, target);
      WsDataCenter.getInstance().clearByPacktId(rspId, target);
    };
  }, []);
  return {
    data,
    error,
  };
};

/**收发 */
export const useWsMutation = <T, R>(rspId: number, sendId: number, decode: any, encode: any) => {
  const { data, error } = useWs<T>(rspId, decode);
  const [isLoading, setIsLoading] = useState(false);
  const promiseRef = useRef<WsPromiseController<T>>({});
  const timeoutTimerRef = useRef<number>();

  const clearTimeoutTimer = useCallback(() => {
    if (timeoutTimerRef.current !== undefined) {
      window.clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = undefined;
    }
  }, []);

  const trigger: (pack: R, options?: WSOptions) => Promise<T> = useCallback((pack: R, options?: WSOptions) => {
    const { timeout = 10 * 1000, timeOutCallback } = options || {};
    clearTimeoutTimer();
    promiseRef.current.reject?.({ code: "WS_REPLACED" });
    return new Promise((res, reject) => {
      promiseRef.current.resolve = res;
      promiseRef.current.reject = reject;
      setIsLoading(true);
      timeoutTimerRef.current = window.setTimeout(() => {
        timeOutCallback && timeOutCallback();
        promiseRef.current.reject?.({ code: "WS_TIMEOUT" });
        promiseRef.current = {};
        timeoutTimerRef.current = undefined;
        setIsLoading(false);
      }, timeout);
      // console.log('========socket send!!!!!!=========')
      // console.log(sendId,pack)
      const didSend = SocketMgr.send(sendId, pack);
      if (!didSend) {
        clearTimeoutTimer();
        promiseRef.current.reject?.({ code: "WS_NOT_CONNECTED" });
        promiseRef.current = {};
        setIsLoading(false);
      }
      // console.log('========socket send!!!!!!=========')
    });
  }, [clearTimeoutTimer]);

  useEffect(() => {
    if (data) {
      promiseRef.current?.resolve?.(data);
      setIsLoading(false);
      clearTimeoutTimer();
      promiseRef.current = {};
    }
  }, [clearTimeoutTimer, data]);
  useEffect(() => {
    if (error) {
      setIsLoading(false);
      promiseRef.current && promiseRef.current?.reject?.(error);
      clearTimeoutTimer();
      promiseRef.current = {};
    }
  }, [clearTimeoutTimer, error]);

  useEffect(() => {
    SocketPackTool.registerPackFunc(
      sendId,
      encode,
    )
    return () => {
      clearTimeoutTimer();
      promiseRef.current.reject?.({ code: "WS_UNMOUNTED" });
      promiseRef.current = {};
    };
  }, [clearTimeoutTimer]);
  return {
    data,
    isLoading,
    trigger,
    error,
  };
};

export class WsDataCenter {
  private cache: Map<number, Map<any, any>> = new Map<number, Map<any, any>>();

  private static instance: WsDataCenter;

  static getInstance(): WsDataCenter {
    if (!this.instance) {
      this.instance = new WsDataCenter();
    }
    return this.instance;
  }

  getDataByPacktId(packtId: number, target) {
    if (this.cache.has(packtId)) {
      const maps = this.cache.get(packtId);
      if (maps?.has(target)) {
        return maps.get(target);
      } else {
        if (maps?.size > 0) {
          const values = maps.values();
          return values.next().value;
        }
      }
    }
  }

  setData(packtId: number, data: any, target) {
    if (this.cache.has(packtId)) {
      let maps = this.cache.get(packtId);
      if (!maps) {
        maps = new Map<any, any>();
        this.cache.set(packtId, maps);
      }
      maps.set(target, data);
    } else {
      const maps = new Map<any, any>();
      maps.set(target, data);
      this.cache.set(packtId, maps);
    }
  }

  clearByPacktId(packetId: number, target: any) {
    if (this.cache.has(packetId)) {
      const maps = this.cache.get(packetId);
      if (maps?.has(target)) {
        maps.delete(target);
      }
      if (maps?.size === 0) {
        this.cache.delete(packetId);
      }
    }
  }
}
